import {
	defaultOperators,
	defaultRuleProcessorSQL,
	Field,
	formatQuery,
	FormatQueryOptions,
	OptionList,
	Operator,
	RuleGroupTypeAny,
	RuleType,
} from 'react-querybuilder'

import { defaultValueProcessorByRule } from './components/customValueProcessorByRule'

export const getTransformedQuery = (query: RuleGroupTypeAny, quoteFieldNames?: string | [string, string]) =>
	formatQuery(query, {
		format: 'sql',
		ruleProcessor: (rule: RuleType, options: FormatQueryOptions) => {
			const quoteFieldNamesWithAsArray = Array.isArray(options.quoteFieldNamesWith)
				? options.quoteFieldNamesWith
				: [options.quoteFieldNamesWith, options.quoteFieldNamesWith]
			if (rule.operator === 'less_than_today') {
				return `${quoteFieldNamesWithAsArray[0]}${rule.field}${quoteFieldNamesWithAsArray[1]} <= CURDATE()`
			}

			if (rule.operator === 'greater_than_today') {
				return `${quoteFieldNamesWithAsArray[0]}${rule.field}${quoteFieldNamesWithAsArray[1]} >= CURDATE()`
			}

			if (rule.operator === 'last_week') {
				return (
					`${quoteFieldNamesWithAsArray[0]}${rule.field}${quoteFieldNamesWithAsArray[1]}` +
					' BETWEEN DATE(' +
					'DATE_SUB(DATE_SUB(NOW(), INTERVAL 1 WEEK), INTERVAL WEEKDAY(DATE_SUB(NOW(), INTERVAL 1 WEEK)) DAY)' +
					') AND DATE(' +
					'DATE_ADD(DATE_SUB(NOW(), INTERVAL 1 WEEK), INTERVAL ABS(WEEKDAY(DATE_SUB(NOW(), INTERVAL 1 WEEK)) - 6) DAY)' +
					')'
				)
			}

			if (rule.operator === 'last_month') {
				return (
					`${quoteFieldNamesWithAsArray[0]}${rule.field}${quoteFieldNamesWithAsArray[1]}` +
					' BETWEEN DATE_FORMAT(LAST_DAY(DATE_SUB(NOW(), INTERVAL 1 MONTH)), "%Y-%m-01")' +
					' AND LAST_DAY(DATE_SUB(NOW(), INTERVAL 1 MONTH))'
				)
			}

			if (rule.operator === 'last_year') {
				return (
					`${quoteFieldNamesWithAsArray[0]}${rule.field}${quoteFieldNamesWithAsArray[1]}` +
					' BETWEEN DATE_FORMAT(MAKEDATE(YEAR(DATE_SUB(NOW(), INTERVAL 1 YEAR)), 1), "%Y-01-01")' +
					' AND DATE_FORMAT(MAKEDATE(YEAR(DATE_SUB(NOW(), INTERVAL 1 YEAR)), 1), "%Y-12-31")'
				)
			}

			if (rule.operator === 'last_quarter') {
				return (
					`${quoteFieldNamesWithAsArray[0]}${rule.field}${quoteFieldNamesWithAsArray[1]}` +
					' BETWEEN DATE_SUB(' +
					'DATE_FORMAT(NOW(), CONCAT("%Y-", LPAD(((CEIL(MONTH(NOW()) / 3) - 1) * 3 + 1), 2, 0), "-01")), ' +
					'INTERVAL 3 MONTH' +
					') AND DATE_SUB(' +
					'LAST_DAY(DATE_FORMAT(NOW(), CONCAT("%Y-", LPAD(((CEIL(MONTH(NOW()) / 3) - 1) * 3 + 3), 2, 0), "-01"))), ' +
					'INTERVAL 3 MONTH' +
					')'
				)
			}

			if (rule.operator === 'isEmpty') {
				return `${quoteFieldNamesWithAsArray[0]}${rule.field}${quoteFieldNamesWithAsArray[1]} = ""`
			}

			if (rule.operator === 'isNotEmpty') {
				return `${quoteFieldNamesWithAsArray[0]}${rule.field}${quoteFieldNamesWithAsArray[1]} != ""`
			}

			options.valueProcessor = defaultValueProcessorByRule

			return defaultRuleProcessorSQL(rule, options)
		},
		quoteFieldNamesWith: quoteFieldNames || '',
		parseNumbers: true,
	}).replace(/\b(and|or|is null|is not null|in|not in|between|not between|like|not like)\b/gi, (word: string) =>
		word.toUpperCase(),
	)

export const getOperators = (field: Field | undefined): OptionList<Operator> => {
	if (!field) {
		return defaultOperators
	}

	switch (field.inputType) {
		case 'text':
			return [
				{ name: '=', label: 'is' },
				{ name: '!=', label: 'is not' },
				{ name: 'isEmpty', label: 'is empty' },
				{ name: 'isNotEmpty', label: 'is not empty' },
				...defaultOperators.filter((op) =>
					[
						'contains',
						'beginsWith',
						'endsWith',
						'doesNotContain',
						'doesNotBeginWith',
						'doesNotEndWith',
						'null',
						'notNull',
						'in',
						'notIn',
					].includes(op.name),
				),
			]
		case 'multiselect':
		case 'number':
			return [
				...defaultOperators.filter((op) =>
					['=', '!=', '<', '<=', '>', '>=', 'in', 'notIn', 'null', 'notNull'].includes(op.name),
				),
			]
		case 'date':
			return [
				...defaultOperators.filter((op) =>
					['=', '!=', '<', '<=', '>', '>=', 'between', 'notBetween', 'null', 'notNull'].includes(op.name),
				),
				{ name: 'less_than_today', label: 'is less than today' },
				{ name: 'greater_than_today', label: 'is greater than today' },
				{ name: 'last_week', label: 'last week' },
				{ name: 'last_month', label: 'last month' },
				{ name: 'last_year', label: 'last year' },
				{ name: 'last_quarter', label: 'last quarter' },
			]
		default:
			return defaultOperators
	}
}
